import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Checkbox,
  Typography,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import { FaTimes, FaCheck } from "react-icons/fa";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import authService from "../../../utils/authService";
import { fetchAllCourses } from "../../../utils/courseUtils";
import { fetchBatchesByCourse } from "../../../utils/batchUtils";

interface CoordinatorInfo {
  coordinator_id: string;
  coordinator_name: string;
  coordinator_email: string;
  coordinator_type: string;
  college_id: string;
  college_name: string;
}

const getCoordinatorOptionId = (coord: {
  id?: string;
  user_id?: string;
}) => String(coord.id || coord.user_id || "");

const MapStudents: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const coordinatorIdFromState = location.state?.coordinator_id || "";
  const coordinatorNameFromState = location.state?.coordinator_name || "";
  const mappingFlow = location.state?.mappingFlow || false;
  const batchesFetchGenerationRef = useRef(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [coordinators, setCoordinators] = useState<any[]>([]);
  const [courses, setCourses] = useState<{ course_id: string; course_name: string }[]>([]);
  const [batches, setBatches] = useState<{ batch_id: string; batch_name: string; course_id?: string }[]>([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState<string>(coordinatorIdFromState || "");
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [students, setStudents] = useState<any[]>([]);
  const [studentCoordinatorMap, setStudentCoordinatorMap] = useState<Record<string, { coordinator_id: string; coordinator_name: string }>>({});
  
  const [coordinatorsByBatches, setCoordinatorsByBatches] = useState<Record<string, any[]>>({});
  
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [studentCoordinatorAssignments, setStudentCoordinatorAssignments] = useState<Record<string, string>>({});
  const [availableBranches, setAvailableBranches] = useState<string[]>([]);
  const [branchFilter, setBranchFilter] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string;
  }>({ key: "", direction: "" });

  const resetCourseAndBatchFilters = () => {
    setSelectedCourse("");
    setSelectedBatch("");
    setCourses([]);
    setBatches([]);
    setStudents([]);
    setStudentCoordinatorMap({});
    setSelectedStudents(new Set());
    setStudentCoordinatorAssignments({});
    setAvailableBranches([]);
    setBranchFilter("");
    setSortConfig({ key: "", direction: "" });
  };

  useEffect(() => {
    if (mappingFlow) {
      fetchCoordinatorsByBatches();
      return;
    }

    const loadData = async () => {
      if (coordinatorIdFromState) {
        resetCourseAndBatchFilters();
        setSelectedCoordinator(coordinatorIdFromState);
        await fetchSubCoordinators(coordinatorIdFromState, coordinatorNameFromState);
        await fetchBatchesByCoordinator(coordinatorIdFromState);
      } else {
        setSelectedCoordinator("");
        resetCourseAndBatchFilters();
        await fetchCoordinators();
      }
    };
    loadData();
  }, [mappingFlow, coordinatorIdFromState, coordinatorNameFromState, location.key]);

  useEffect(() => {
    if (mappingFlow) {
      if (selectedCourse) {
      } else {
        setSelectedBatch("");
        setCoordinators([]);
      }
    }
  }, [selectedCourse, mappingFlow]);

  useEffect(() => {
    if (mappingFlow && selectedBatch && coordinatorsByBatches[selectedBatch]) {
      setCoordinators(coordinatorsByBatches[selectedBatch] || []);
    }
  }, [selectedBatch, mappingFlow, coordinatorsByBatches]);

  const fetchCoordinatorsByBatches = async () => {
    try {
      setLoading(true);
      const response = await authService.get('admin/get_all_coordinators_by_batches/');
      const responseData = response.data || {};
      
      const coursesData = responseData.courses || [];
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      
      const batchesData = responseData.batches || {};
      const batchesList: { batch_id: string; batch_name: string; course_id?: string }[] = [];
      
      Object.keys(batchesData).forEach((courseId) => {
        const courseBatches = batchesData[courseId];
        if (Array.isArray(courseBatches)) {
          courseBatches.forEach((b: any) => {
            batchesList.push({
              batch_id: String(b.batch_id || b.id || ''),
              batch_name: String(b.batch_name || b.name || ''),
              course_id: String(b.course_id || courseId || '')
            });
          });
        }
      });
      
      const coordinatorsData = responseData.coordinators || {};
      const coordByBatch: Record<string, any[]> = {};
      
      Object.keys(coordinatorsData).forEach((batchId) => {
        const batchCoordinators = coordinatorsData[batchId];
        if (Array.isArray(batchCoordinators)) {
          coordByBatch[batchId] = batchCoordinators.filter((c: any) => !c.type || c.type === 'SC');
        }
      });
      
      setBatches(batchesList);
      setCoordinatorsByBatches(coordByBatch);
    } catch (e) {
      console.error('Error fetching coordinators by batches', e);
      setError('Error loading coordinators by batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchesByCoordinator = async (userId: string) => {
    if (!userId) return;
    const fetchGeneration = ++batchesFetchGenerationRef.current;
    try {
      setLoading(true);
      const response = await authService.get(`admin/get_batch_ids_by_coordinator/${userId}/`);
      if (fetchGeneration !== batchesFetchGenerationRef.current) return;

      const responseData = response.data || {};
      
      const coursesData = responseData.courses || [];
      const batchesData = responseData.batches || {};
      const batchesList: { batch_id: string; batch_name: string; course_id?: string }[] = [];
      
      if (Array.isArray(batchesData)) {
        batchesList.push(...batchesData.map((b: any) => ({
          batch_id: String(b.batch_id || b.id || ''),
          batch_name: String(b.batch_name || b.name || ''),
          course_id: String(b.course_id || '')
        })));
      } else {
        Object.keys(batchesData).forEach((courseId) => {
          const courseBatches = batchesData[courseId];
          if (Array.isArray(courseBatches)) {
            courseBatches.forEach((b: any) => {
              batchesList.push({
                batch_id: String(b.batch_id || b.id || ''),
                batch_name: String(b.batch_name || b.name || ''),
                course_id: String(b.course_id || courseId || '')
              });
            });
          }
        });
      }

      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setBatches(batchesList);
    } catch (e) {
      if (fetchGeneration !== batchesFetchGenerationRef.current) return;
      console.error('Error fetching batches by coordinator', e);
      setError('Error loading batches for coordinator');
    } finally {
      if (fetchGeneration === batchesFetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchCoordinators = async () => {
    try {
      const response = await authService.post('admin/get_all_coordinator/', { batch_ids: [] }, {
        headers: { 'Content-Type': 'application/json' },
      });
      const scCoordinators = (response.data.coordinators || []).filter((c: any) => c.type === 'SC');
      setCoordinators(scCoordinators);
    } catch (e) {
      console.error('Error fetching coordinators', e);
    }
  };

  const fetchSubCoordinators = async (
    ensureCoordinatorId?: string,
    ensureCoordinatorName?: string
  ) => {
    try {
      const response = await authService.get('admin/get_all_sub_coordinators/');
      const subCoordinators = response.data?.coordinators || response.data || [];
      let list = Array.isArray(subCoordinators) ? subCoordinators : [];

      if (
        ensureCoordinatorId &&
        !list.some((c) => getCoordinatorOptionId(c) === ensureCoordinatorId)
      ) {
        list = [
          {
            id: ensureCoordinatorId,
            user_id: ensureCoordinatorId,
            name: ensureCoordinatorName || "Coordinator",
          },
          ...list,
        ];
      }

      setCoordinators(list);
    } catch (e) {
      console.error('Error fetching sub-coordinators', e);
      setError('Error loading sub-coordinators');
    }
  };

  const fetchCourses = async () => {
    try {
      const data = await fetchAllCourses();
      setCourses(data);
    } catch (e) {
      console.error('Error fetching courses', e);
    }
  };

  const fetchBatchesList = async (course_id: string) => {
    try {
      const data = await fetchBatchesByCourse(course_id);
      setBatches(data);
    } catch (e) {
      console.error('Error fetching batches', e);
    }
  };

  const fetchStudents = useCallback(async () => {
    if (!selectedBatch || !selectedCourse) return;
    try {
      setLoading(true);
      setError("");
      const response = await authService.post('admin/list_students_by_batch/', {
        course_id: selectedCourse,
        batch_id: selectedBatch
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      const studentsData = response.data.students || response.data || [];
      const studentList = Array.isArray(studentsData) ? studentsData : [];
      setStudents(studentList);
      setBranchFilter("");

      const branchesFromApi = response.data.branches;
      let branchOptions: string[] = [];
      if (Array.isArray(branchesFromApi) && branchesFromApi.length > 0) {
        branchOptions = branchesFromApi
          .map((b: string | { branch?: string; branch_name?: string; name?: string }) =>
            typeof b === "string"
              ? b.trim()
              : String(b.branch || b.branch_name || b.name || "").trim()
          )
          .filter(Boolean);
      } else {
        branchOptions = Array.from(
          new Set(
            studentList
              .map((s: { branch?: string }) => String(s.branch || "").trim())
              .filter(Boolean)
          )
        );
      }
      setAvailableBranches(
        Array.from(new Set(branchOptions)).sort((a, b) => a.localeCompare(b))
      );

      if (studentList.length > 0) {
        const map: Record<string, { coordinator_id: string; coordinator_name: string }> = {};
        studentList.forEach((student: any) => {
          const studentId = String(student.student_id || student.id || student.stuId || '');
          if (studentId && student.coordinator_id && student.coordinator_name) {
            map[studentId] = {
              coordinator_id: String(student.coordinator_id),
              coordinator_name: String(student.coordinator_name)
            };
          }
        });
        setStudentCoordinatorMap(map);
      }
    } catch (e) {
      console.error('Error fetching students', e);
      setError('Error loading students');
    } finally {
      setLoading(false);
    }
  }, [selectedBatch, selectedCourse]);

  useEffect(() => {
    if (selectedBatch && selectedCourse) {
      fetchStudents();
    } else {
      setStudents([]);
      setStudentCoordinatorMap({});
      setSelectedStudents(new Set());
      setStudentCoordinatorAssignments({});
      setAvailableBranches([]);
      setBranchFilter("");
      setSortConfig({ key: "", direction: "" });
    }
  }, [selectedBatch, selectedCourse, fetchStudents]);

  const filteredStudents = branchFilter
    ? students.filter(
        (s) => String(s.branch || "").trim() === branchFilter
      )
    : students;

  const getStudentId = (student: { student_id?: string; id?: string; stuId?: string }) =>
    String(student.student_id || student.id || student.stuId || "");

  const getStudentName = (student: { student_name?: string }) =>
    String(student.student_name || "").trim();

  const getCoordinatorDisplayName = (student: {
    coordinator_name?: string;
    student_id?: string;
    id?: string;
    stuId?: string;
  }) => {
    const studentId = getStudentId(student);
    const mapping = studentCoordinatorMap[studentId];
    const assignedCoordinatorId = studentCoordinatorAssignments[studentId];

    if (selectedStudents.has(studentId) && assignedCoordinatorId) {
      const coord = coordinators.find(
        (c) => getCoordinatorOptionId(c) === assignedCoordinatorId
      );
      if (coord) {
        return (
          coord.name ||
          `${coord.user_first_name || ""} ${coord.user_last_name || ""}`.trim()
        );
      }
    }

    return String(
      student.coordinator_name || mapping?.coordinator_name || ""
    ).trim();
  };

  const handleSort = (key: string) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const compareSortValues = (aVal: string, bVal: string) => {
    const aEmpty = !aVal;
    const bEmpty = !bVal;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return sortConfig.direction === "ascending" ? -1 : 1;
    if (bEmpty) return sortConfig.direction === "ascending" ? 1 : -1;
    const comparison = aVal.localeCompare(bVal, undefined, {
      sensitivity: "base",
    });
    return sortConfig.direction === "ascending" ? comparison : -comparison;
  };

  const displayStudents = () => {
    const list = [...filteredStudents];

    if (sortConfig.key === "name") {
      return list.sort((a, b) =>
        compareSortValues(getStudentName(a), getStudentName(b))
      );
    }

    if (sortConfig.key === "coordinator") {
      return list.sort((a, b) =>
        compareSortValues(
          getCoordinatorDisplayName(a),
          getCoordinatorDisplayName(b)
        )
      );
    }

    return list.sort((a, b) => {
      const studentIdA = getStudentId(a);
      const studentIdB = getStudentId(b);
      const hasCoordinatorA = !!studentCoordinatorMap[studentIdA];
      const hasCoordinatorB = !!studentCoordinatorMap[studentIdB];
      if (!hasCoordinatorA && hasCoordinatorB) return -1;
      if (hasCoordinatorA && !hasCoordinatorB) return 1;
      return 0;
    });
  };

  const sortableHeaderStyle = {
    backgroundColor: "#f5f5f5",
    borderRight: "1px solid #e0e0e0",
    fontWeight: 600,
    fontSize: "12px",
    padding: "8px 12px",
    position: "sticky" as const,
    top: 0,
    zIndex: 10,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  };

  const renderSortableHeader = (
    label: string,
    sortKey: string,
    minWidth: string
  ) => (
    <TableCell
      onClick={() => handleSort(sortKey)}
      style={{ ...sortableHeaderStyle, minWidth }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <span>{label}</span>
        <span style={{ marginLeft: "4px", display: "flex" }}>
          {sortConfig.key === sortKey ? (
            sortConfig.direction === "ascending" ? (
              <PiSortAscendingLight size={16} />
            ) : (
              <PiSortDescendingLight size={16} />
            )
          ) : (
            <HiOutlineDotsVertical size={16} />
          )}
        </span>
      </Box>
    </TableCell>
  );

  const handleCheckboxChange = (studentId: string, checked: boolean) => {
    if (checked) {
      if (!selectedCoordinator) {
        setError('Please select a coordinator from the filter dropdown');
        return;
      }
      
      setSelectedStudents(prev => new Set(prev).add(studentId));
      setStudentCoordinatorAssignments(prev => ({
        ...prev,
        [studentId]: selectedCoordinator
      }));
    } else {
      setSelectedStudents(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
      const newAssignments = { ...studentCoordinatorAssignments };
      delete newAssignments[studentId];
      setStudentCoordinatorAssignments(newAssignments);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      if (!selectedCoordinator) {
        setError('Please select a coordinator from the filter dropdown');
        return;
      }
      
      const allStudentIds = displayStudents()
        .map((s) => getStudentId(s))
        .filter(Boolean);
      const newSelected = new Set(allStudentIds);
      const newAssignments: Record<string, string> = {};
      allStudentIds.forEach(studentId => {
        newAssignments[studentId] = selectedCoordinator;
      });
      setSelectedStudents(newSelected);
      setStudentCoordinatorAssignments(newAssignments);
    } else {
      setSelectedStudents(new Set());
      setStudentCoordinatorAssignments({});
    }
  };

  const allStudentsSelected =
    displayStudents().length > 0 &&
    displayStudents().every((s) => selectedStudents.has(getStudentId(s)));

  const handleSave = async () => {
    if (selectedStudents.size === 0) {
      setError('Please select at least one student');
      return;
    }
    
    const studentIds = Array.from(selectedStudents);
    const assignmentsWithCoordinator = studentIds.filter(id => studentCoordinatorAssignments[id]);
    
    if (assignmentsWithCoordinator.length === 0) {
      setError('Please assign at least one student to a coordinator');
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      const coordinatorGroups: Record<string, string[]> = {};
      studentIds.forEach(studentId => {
        const coordinatorId = studentCoordinatorAssignments[studentId];
        if (coordinatorId) {
          if (!coordinatorGroups[coordinatorId]) {
            coordinatorGroups[coordinatorId] = [];
          }
          coordinatorGroups[coordinatorId].push(studentId);
        }
      });
      
      const payload = {
        data: Object.keys(coordinatorGroups).map(coordinatorId => ({
          coordinator_id: coordinatorId,
          student_ids: coordinatorGroups[coordinatorId]
        }))
      };
      
      await authService.post('admin/map_students_to_coordinator/', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      const updatedMap = { ...studentCoordinatorMap };
      Object.keys(coordinatorGroups).forEach(coordinatorId => {
        const coordinator = coordinators.find(
          (c) => getCoordinatorOptionId(c) === coordinatorId
        );
        const coordinatorName = coordinator?.name || `${coordinator?.user_first_name || ''} ${coordinator?.user_last_name || ''}`.trim() || '';
        coordinatorGroups[coordinatorId].forEach(studentId => {
          updatedMap[studentId] = {
            coordinator_id: coordinatorId,
            coordinator_name: coordinatorName
          };
        });
      });
      setStudentCoordinatorMap(updatedMap);
      
      setSelectedStudents(new Set());
      setStudentCoordinatorAssignments({});
      
      await fetchStudents();
    } catch (e) {
      console.error('Error saving student assignments', e);
      setError('Error saving student assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async (studentId: string) => {
    const mapping = studentCoordinatorMap[studentId];
    if (!mapping) return;
    
    try {
      setLoading(true);
      setError("");
      await authService.post('admin/unmap_student_from_coordinator/', {
        coordinator_id: mapping.coordinator_id,
        student_id: studentId
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      const newMap = { ...studentCoordinatorMap };
      delete newMap[studentId];
      setStudentCoordinatorMap(newMap);
    } catch (e) {
      console.error('Error unassigning student', e);
      setError('Error unassigning student');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px", height: `calc(100vh - 90px)` }}>
      {loading && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
        </div>
      )}

      <Card>
        <CardContent
          style={{
            height: `calc(100vh - 105px)`,
            overflow: "hidden",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              {mappingFlow ? (
                <>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      Course
                    </InputLabel>
                    <Select
                      value={selectedCourse}
                      onChange={(e: SelectChangeEvent<string>) => {
                        setSelectedCourse(e.target.value);
                        setSelectedBatch("");
                        setSelectedCoordinator("");
                      }}
                      label="Course"
                      disabled={loading}
                      style={{
                        height: "36px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                      }}
                    >
                      <MenuItem
                        value=""
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        Select Course
                      </MenuItem>
                      {courses.map((course) => (
                        <MenuItem
                          key={course.course_id}
                          value={course.course_id}
                          style={{ fontSize: "14px", fontFamily: "inherit" }}
                        >
                          {course.course_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      Batch
                    </InputLabel>
                    <Select
                      value={selectedBatch}
                      onChange={(e: SelectChangeEvent<string>) => {
                        setSelectedBatch(e.target.value);
                        setSelectedCoordinator("");
                      }}
                      label="Batch"
                      disabled={!selectedCourse || loading}
                      style={{
                        height: "36px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                      }}
                    >
                      <MenuItem
                        value=""
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        Select Batch
                      </MenuItem>
                      {batches
                        .filter((batch) => !selectedCourse || batch.course_id === selectedCourse)
                        .map((batch) => (
                          <MenuItem
                            key={batch.batch_id}
                            value={batch.batch_id}
                            style={{ fontSize: "14px", fontFamily: "inherit" }}
                          >
                            {batch.batch_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      Coordinator
                    </InputLabel>
                    <Select
                      value={selectedCoordinator}
                      onChange={(e: SelectChangeEvent<string>) => setSelectedCoordinator(e.target.value)}
                      label="Coordinator"
                      disabled={!selectedBatch || loading}
                      style={{
                        height: "36px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                      }}
                    >
                      <MenuItem
                        value=""
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        Select Coordinator
                      </MenuItem>
                      {coordinators.map((coord) => {
                        const id = getCoordinatorOptionId(coord);
                        const name = coord.name || `${coord.user_first_name || ''} ${coord.user_last_name || ''}`.trim();
                        return (
                          <MenuItem
                            key={id}
                            value={id}
                            style={{ fontSize: "14px", fontFamily: "inherit" }}
                          >
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      Coordinator
                    </InputLabel>
                    <Select
                      value={selectedCoordinator}
                      onChange={async (e: SelectChangeEvent<string>) => {
                        const userId = e.target.value;
                        setSelectedCoordinator(userId);
                        resetCourseAndBatchFilters();
                        if (userId && selectedStudents.size > 0) {
                          const updatedAssignments = { ...studentCoordinatorAssignments };
                          selectedStudents.forEach(studentId => {
                            updatedAssignments[studentId] = userId;
                          });
                          setStudentCoordinatorAssignments(updatedAssignments);
                        }
                        if (userId) {
                          const coord = coordinators.find(
                            (c) => getCoordinatorOptionId(c) === userId
                          );
                          const coordName =
                            coord?.name ||
                            `${coord?.user_first_name || ""} ${coord?.user_last_name || ""}`.trim();
                          await fetchSubCoordinators(userId, coordName);
                          await fetchBatchesByCoordinator(userId);
                        }
                      }}
                      label="Coordinator"
                      disabled={loading}
                      style={{
                        height: "36px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                      }}
                    >
                      <MenuItem
                        value=""
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        Select Coordinator
                      </MenuItem>
                      {coordinators.map((coord) => {
                        const id = getCoordinatorOptionId(coord);
                        const name = coord.name || `${coord.user_first_name || ''} ${coord.user_last_name || ''}`.trim();
                        return (
                          <MenuItem
                            key={id}
                            value={id}
                            style={{ fontSize: "14px", fontFamily: "inherit" }}
                          >
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      Course
                    </InputLabel>
                    <Select
                      value={selectedCourse}
                      onChange={(e: SelectChangeEvent<string>) => {
                        setSelectedCourse(e.target.value);
                        setSelectedBatch("");
                      }}
                      label="Course"
                      disabled={!selectedCoordinator || loading}
                      style={{
                        height: "36px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                      }}
                    >
                      <MenuItem
                        value=""
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        Select Course
                      </MenuItem>
                      {courses.map((course) => (
                        <MenuItem
                          key={course.course_id}
                          value={course.course_id}
                          style={{ fontSize: "14px", fontFamily: "inherit" }}
                        >
                          {course.course_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      Batch
                    </InputLabel>
                    <Select
                      value={selectedBatch}
                      onChange={(e: SelectChangeEvent<string>) => setSelectedBatch(e.target.value)}
                      label="Batch"
                      disabled={!selectedCourse || loading}
                      style={{
                        height: "36px",
                        fontSize: "14px",
                        fontFamily: "inherit",
                      }}
                    >
                      <MenuItem
                        value=""
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        Select Batch
                      </MenuItem>
                      {batches
                        .filter((batch) => !selectedCourse || batch.course_id === selectedCourse)
                        .map((batch) => (
                          <MenuItem
                            key={batch.batch_id}
                            value={batch.batch_id}
                            style={{ fontSize: "14px", fontFamily: "inherit" }}
                          >
                            {batch.batch_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={selectedStudents.size === 0 || loading}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                }}
              >
                Save
              </Button>
            </div>
          </Box>

          <TableContainer
            component={Paper}
            style={{ 
              marginTop: "8px", 
              flexGrow: 1, 
              overflow: "auto",
              position: "relative"
            }}
          >
            {!selectedBatch || !selectedCourse ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Typography style={{ color: "#666", fontSize: "14px", fontStyle: "italic" }}>
                  Please select {mappingFlow ? 'course and batch' : 'course and batch'} to view students.
                </Typography>
              </Box>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "8px 12px",
                        minWidth: "50px",
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      <Checkbox
                        checked={allStudentsSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={!selectedCoordinator || displayStudents().length === 0}
                        size="small"
                        indeterminate={selectedStudents.size > 0 && !allStudentsSelected}
                      />
                    </TableCell>
                    {renderSortableHeader("Name", "name", "120px")}
                    <TableCell
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "4px 8px",
                        minWidth: "140px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={availableBranches.length === 0}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InputLabel
                          style={{ fontSize: "12px", fontFamily: "inherit" }}
                        >
                          Branch
                        </InputLabel>
                        <Select
                          value={branchFilter}
                          onChange={(e: SelectChangeEvent<string>) =>
                            setBranchFilter(e.target.value)
                          }
                          label="Branch"
                          style={{
                            height: "32px",
                            fontSize: "12px",
                            fontFamily: "inherit",
                            backgroundColor: "#fff",
                          }}
                        >
                          <MenuItem
                            value=""
                            style={{ fontSize: "12px", fontFamily: "inherit" }}
                          >
                            All Branches
                          </MenuItem>
                          {availableBranches.map((branch) => (
                            <MenuItem
                              key={branch}
                              value={branch}
                              style={{ fontSize: "12px", fontFamily: "inherit" }}
                            >
                              {branch}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    {renderSortableHeader("Coordinator", "coordinator", "150px")}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        style={{
                          padding: "20px",
                        }}
                      >
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        style={{
                          padding: "20px",
                          color: "#d32f2f",
                          fontSize: "14px",
                        }}
                      >
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : students.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        style={{
                          padding: "20px",
                          color: "#666",
                          fontSize: "14px",
                          fontStyle: "italic",
                        }}
                      >
                        No students found
                      </TableCell>
                    </TableRow>
                  ) : displayStudents().length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        style={{
                          padding: "20px",
                          color: "#666",
                          fontSize: "14px",
                          fontStyle: "italic",
                        }}
                      >
                        No students found for the selected branch
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayStudents().map((student: any, index: number) => {
                      const studentId = getStudentId(student);
                      const mapping = studentCoordinatorMap[studentId];
                      const isAssigned = !!mapping;
                      const isAssignedToSelected = isAssigned && mapping.coordinator_id === selectedCoordinator;
                      const isSelected = selectedStudents.has(studentId);
                      
                      const assignedCoordinatorId = isSelected ? studentCoordinatorAssignments[studentId] : null;
                      const currentCoordinatorId = mapping?.coordinator_id || '';
                      const hasDifferentCoordinator = isSelected && assignedCoordinatorId && currentCoordinatorId && currentCoordinatorId !== assignedCoordinatorId;
                      
                      const oldCoordinator = hasDifferentCoordinator
                        ? coordinators.find(
                            (c) => getCoordinatorOptionId(c) === currentCoordinatorId
                          )
                        : null;
                      const oldCoordinatorName = oldCoordinator 
                        ? (oldCoordinator.name || `${oldCoordinator.user_first_name || ''} ${oldCoordinator.user_last_name || ''}`.trim())
                        : (hasDifferentCoordinator ? (mapping?.coordinator_name || 'Unassigned') : '');
                      
                      const newCoordinator =
                        isSelected && assignedCoordinatorId
                          ? coordinators.find(
                              (c) =>
                                getCoordinatorOptionId(c) === assignedCoordinatorId
                            )
                          : null;
                      const newCoordinatorName = newCoordinator 
                        ? (newCoordinator.name || `${newCoordinator.user_first_name || ''} ${newCoordinator.user_last_name || ''}`.trim())
                        : '';
                      
                      return (
                        <TableRow
                          key={studentId}
                          style={{
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                        >
                          <TableCell
                            style={{
                              padding: "6px 12px",
                              textAlign: "center",
                              borderRight: "1px solid #e0e0e0",
                            }}
                          >
                            <Checkbox
                              checked={selectedStudents.has(studentId)}
                              onChange={(e) => handleCheckboxChange(studentId, e.target.checked)}
                              disabled={!selectedCoordinator}
                              size="small"
                            />
                          </TableCell>
                          <TableCell
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              borderRight: "1px solid #e0e0e0",
                              color: "#333",
                            }}
                          >
                            {student.student_name || '-'}
                          </TableCell>
                          <TableCell
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              borderRight: "1px solid #e0e0e0",
                              color: "#333",
                            }}
                          >
                            {student.branch || '-'}
                          </TableCell>
                          <TableCell
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              borderRight: "1px solid #e0e0e0",
                              color: "#333",
                            }}
                          >
                            {hasDifferentCoordinator ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <FaTimes style={{ color: "#dc3545", marginRight: "6px", fontSize: "14px" }} />
                                  <span>{oldCoordinatorName}</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <FaCheck style={{ color: "#28a745", marginRight: "6px", fontSize: "14px" }} />
                                  <span>{newCoordinatorName}</span>
                                </div>
                              </div>
                            ) : isSelected && assignedCoordinatorId ? (
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <FaCheck style={{ color: "#28a745", marginRight: "6px", fontSize: "14px" }} />
                                <span>{newCoordinatorName || '-'}</span>
                              </div>
                            ) : (
                              student.coordinator_name || mapping?.coordinator_name || '-'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default MapStudents;

